import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { hubDate, MAX_TEXT, OUTPUT_KINDS, ROLE_OPTIONS, STATUS_OPTIONS, TASK_TYPES, type ActionItem, type GanttBundle, type GanttNode, type GanttOutput } from '@pathonline/shared';
import { useState } from 'react';
import { getJson, sendJson, useReadOnly, useRun, type Note } from '../api.ts';
import { FolderModal } from '../folders/FolderModal.tsx';
import { folderLook } from '../folders/model.ts';
import { changedValue, useSentList } from '../inputs.ts';
import { dueOnBlur, fmtDay, sheetNote, useAddAction, useGone, useSave, withAction } from './ActionPopover.tsx';
import { byRole, canEditNode, canParent, daysLeft, fmtLeft, httpCheck, httpLinkOf, isGrant, peopleByRole, typeLabel } from './model.ts';
import { ThreadModal } from './ThreadModal.tsx';

const enc = encodeURIComponent;

/** Links only the Kumu map showed (MatchMaking_Phase2.gs:205-209): Azure property, label, placeholder. */
const KUMU_LINKS = [
  { field: 'OneNote', key: 'oneNote', label: 'OneNote', placeholder: 'https://… OneNote notebook or page' },
  { field: 'WebOfScience', key: 'webOfScience', label: 'Web of Science', placeholder: 'https://www.webofscience.com/…' },
  { field: 'Website', key: 'website', label: 'Website', placeholder: 'https://…' },
] as const;

interface Props {
  bundle: GanttBundle;
  nodeId: string;
  onClose: () => void;
  /** After a delete or convert: the task is gone from the list. */
  onGone: () => void;
}

/** Details of one task or grant; editable with Gantt write. */
export function NodePanel({ bundle: b, nodeId, onClose, onGone }: Props) {
  const n = b.nodes.find((x) => x.id === nodeId);
  // One status line under the panel title.
  const { run, busy, msg } = useRun(undefined, { saving: 'Saving…', ok: 'Saved' });
  const [thread, setThread] = useState(false);
  const [folder, setFolder] = useState(false);
  const readOnly = useReadOnly();
  // Here, not in Footer: the panel stays open on a deleted task ("not on your Gantt"), the Footer does not.
  const gone = useGone();
  if (!n) {
    return (
      <aside className="panel" aria-label="Task details">
        <button type="button" className="panel-close" onClick={onClose} aria-label="Close">×</button>
        <p className="muted">{nodeId} is not on your Gantt.</p>
      </aside>
    );
  }
  const ed = canEditNode(b, n);
  const grant = isGrant(n);
  const setField = (field: string, value: string, ok = 'Saved', failed?: () => void) => run(() => sendJson('PATCH', `/api/nodes/${enc(n.id)}`, { field, value }), ok, undefined, failed);
  const comments = b.commentCounts[n.id] ?? 0;
  // Its folder, shown as the folder on its bar is (folderLook): not an empty one nobody may add to.
  const files = folderLook(b.folders?.[n.id], readOnly) ? b.folders?.[n.id] : undefined;
  const left = daysLeft(n.end);

  return (
    <aside className={`panel editor ${ed ? '' : 'ro'}`} aria-label={grant ? 'Grant details' : 'Task details'}>
      <button type="button" className="panel-close" onClick={onClose} aria-label="Close">×</button>
      <p className="panel-kicker">
        <span className={`g-type t-${typeLabel(n.type).toLowerCase()}`}>{typeLabel(n.type)}</span> {n.id}
        {n.focus && <span title="Focus (0–5), as the Kumu map showed it"> · Focus {n.focus}</span>}
      </p>
      {ed ? (
        <input
          key={`l:${n.id}:${n.label}`}
          className="ed-title"
          defaultValue={n.label}
          aria-label="Name"
          onBlur={(e) => {
            const v = e.target.value.trim();
            // A blank name is not saved (a task needs a name): the stored one comes back.
            if (!v) e.target.value = n.label;
            else if (v !== n.label) setField('Label', v);
          }}
        />
      ) : (
        <h2>{n.label}</h2>
      )}
      {msg && <p className={`ed-msg ${msg.bad ? 'error' : 'ok'}`} role="status">{msg.text}</p>}

      <p className="ed-links">
        {(b.canComment || comments > 0) && (
          <button type="button" className="link" onClick={() => setThread(true)}>💬 Comments{comments ? ` (${comments})` : ''}</button>
        )}
        {files && (
          <button type="button" className="link" onClick={() => setFolder(true)}>📁 Files{files.files ? ` (${files.files})` : ''}</button>
        )}
        {n.todoist && <a href={n.todoist} target="_blank" rel="noopener noreferrer">Todoist ↗</a>}
        {KUMU_LINKS.map((l) => {
          const v = httpLinkOf(n[l.key]);
          return v && <a key={l.key} href={v} target="_blank" rel="noopener noreferrer">{l.label} ↗</a>;
        })}
      </p>

      {!grant && (
        <div className="ed-row">
          <label>
            <span>Category</span>
            <select value={n.type || 'Topic'} disabled={!ed || busy} onChange={(e) => setField('NodeType', e.target.value, 'Category updated')}>
              {TASK_TYPES.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
            </select>
          </label>
          <label>
            <span>Status</span>
            <select value={n.status} disabled={!ed || busy} onChange={(e) => setField('Status', e.target.value, `Status → ${e.target.value}`)}>
              {STATUS_OPTIONS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
        </div>
      )}

      <label className="ed-field">
        <span>Description</span>
        <textarea key={`d:${n.id}:${n.description}`} rows={4} maxLength={MAX_TEXT} defaultValue={n.description} readOnly={!ed} onBlur={(e) => ed && e.target.value !== n.description && setField('Description', e.target.value)} />
      </label>
      {/* Nodes.Notes: only the Sheet showed it. */}
      {(ed || n.notes) && (
        <label className="ed-field">
          <span>Notes</span>
          <textarea key={`n:${n.id}:${n.notes ?? ''}`} rows={3} maxLength={MAX_TEXT} defaultValue={n.notes ?? ''} readOnly={!ed} onBlur={(e) => ed && e.target.value !== (n.notes ?? '') && setField('Notes', e.target.value)} />
        </label>
      )}

      {ed && (
        <>
          <LinkField label="Todoist link" placeholder="https://app.todoist.com/app/task/… or /project/…" value={n.todoist} check={(v) => !v || /todoist\.com\//i.test(v) ? '' : 'Paste a Todoist link (todoist.com/…)'} onSave={(v) => setField('Todoist', v, v ? 'Todoist link saved' : 'Todoist link removed')} id={n.id} />
          {KUMU_LINKS.map((l) => (
            <LinkField key={l.field} label={l.label} placeholder={l.placeholder} value={n[l.key] ?? ''} check={httpCheck} onSave={(v) => setField(l.field, v, `${l.label} link ${v ? 'saved' : 'removed'}`)} id={n.id} />
          ))}
        </>
      )}

      <div className="ed-row">
        <label>
          <span>Start</span>
          <input key={`s:${n.id}:${n.start}`} type="date" defaultValue={n.start} readOnly={!ed} onBlur={(e) => { const v = ed ? changedValue(e.target, n.start) : null; if (v !== null) setField('StartDate', v); }} />
        </label>
        <label>
          <span>End</span>
          <input key={`e:${n.id}:${n.end}`} type="date" defaultValue={n.end} readOnly={!ed} onBlur={(e) => { const v = ed ? changedValue(e.target, n.end) : null; if (v !== null) setField('EndDate', v); }} />
        </label>
      </div>
      {left !== null && <p className="muted small">{fmtLeft(left)}</p>}

      {!grant && <ParentSection b={b} n={n} ed={ed} busy={busy} setField={setField} />}
      {!grant && <GrantsSection b={b} n={n} ed={ed} busy={busy} setField={setField} />}
      {!grant && <OutputsSection b={b} n={n} run={run} busy={busy} />}
      <ActionsSection b={b} n={n} />
      <TeamSection b={b} n={n} ed={ed} run={run} busy={busy} />

      {ed && <Footer b={b} n={n} run={run} busy={busy} onGone={() => { if (!gone.current) onGone(); }} />}
      {thread && <ThreadModal b={b} node={n} onClose={() => setThread(false)} />}
      {folder && files && <FolderModal nodeId={n.id} label={n.label} onClose={() => setFolder(false)} />}
    </aside>
  );
}

function LinkField({ id, label, placeholder, value, check, onSave }: {
  id: string; label: string; placeholder: string; value: string; check: (v: string) => string; onSave: (v: string) => void;
}) {
  const [err, setErr] = useState('');
  const open = httpLinkOf(value);
  return (
    <label className="ed-field">
      <span>{label}</span>
      <span className="ed-link">
        <input
          key={`${label}:${id}:${value}`}
          defaultValue={value}
          placeholder={placeholder}
          onBlur={(e) => {
            const v = e.target.value.trim();
            // Unchanged: nothing to check or save (an imported OneNote cell holds two links).
            const bad = v === value ? '' : check(v);
            setErr(bad);
            if (!bad && v !== value) onSave(v);
          }}
        />
        {open && <a href={open} target="_blank" rel="noopener noreferrer">open</a>}
      </span>
      {err && <span className="error small">{err}</span>}
    </label>
  );
}

type SetField = (field: string, value: string, ok?: string, failed?: () => void) => void;
type Run = ReturnType<typeof useRun>['run'];

function ParentSection({ b, n, ed, busy, setField }: { b: GanttBundle; n: GanttNode; ed: boolean; busy: boolean; setField: SetField }) {
  const parent = n.parentId ? b.nodes.find((x) => x.id === n.parentId) : undefined;
  const options = b.nodes.filter((c) => !isGrant(c) && canParent(b, n.id, c.id)).sort((x, y) => x.label.localeCompare(y.label));
  return (
    <section className="ed-sec">
      <h3>Parent task</h3>
      {n.parentId ? (
        <div className="ed-chip">
          {parent?.label ?? n.parentId}
          {ed && <button type="button" className="ed-x" disabled={busy} aria-label="Clear parent" onClick={() => setField('ParentID', '', 'Parent cleared')}>×</button>}
        </div>
      ) : (
        <p className="muted small">No parent — a top-level task.</p>
      )}
      {ed && (
        <select value="" disabled={busy} aria-label="Set parent" onChange={(e) => e.target.value && setField('ParentID', e.target.value, 'Parent set')}>
          <option value="">+ set parent…</option>
          {options.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      )}
    </section>
  );
}

function GrantsSection({ b, n, ed, busy, setField }: { b: GanttBundle; n: GanttNode; ed: boolean; busy: boolean; setField: SetField }) {
  const grants = b.nodes.filter(isGrant).sort((x, y) => x.label.localeCompare(y.label));
  const [pick, setPick] = useState('');
  // n.grantIds; while a save is on its way, the grants as sent.
  const sent = useSentList(n.grantIds);
  const linked = sent.list;
  const save = (ids: string[], ok: string) => setField('FundingProjectID', ids.join(','), ok, sent.begin(ids));
  return (
    <section className="ed-sec">
      <h3>Grants</h3>
      {linked.length === 0 && <p className="muted small">No grants linked.</p>}
      {linked.map((g) => (
        <div key={g} className="ed-chip">
          ◆ {b.nodes.find((x) => x.id === g)?.label ?? g}
          {ed && <button type="button" className="ed-x" disabled={busy} aria-label="Remove grant" onClick={() => save(linked.filter((x) => x !== g), 'Grant removed')}>×</button>}
        </div>
      ))}
      {ed && (
        <div className="ed-add">
          <select value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Grant to link">
            <option value="">+ grant…</option>
            {grants.filter((g) => !linked.includes(g.id)).map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
          </select>
          <button type="button" className="btn small" disabled={!pick || busy} onClick={() => { save([...linked, pick], 'Grant linked'); setPick(''); }}>Add</button>
        </div>
      )}
    </section>
  );
}

function OutputsSection({ b, n, run, busy }: { b: GanttBundle; n: GanttNode; run: Run; busy: boolean }) {
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<string>(OUTPUT_KINDS[0]);
  const [bad, setBad] = useState('');
  if (b.outputsAccess === 'none') return null;
  const list = b.outputs.filter((o) => o.nodeId === n.id);
  const canWrite = b.outputsAccess === 'write';
  // Inline rename (old saveOutTitle, DP:7442-7450): the add's 3-character rule; refused or failed, the stored title comes back.
  const rename = (o: GanttOutput, el: HTMLInputElement) => {
    const v = el.value.trim();
    setBad('');
    if (v === o.title) {
      el.value = o.title;
      return;
    }
    if (v.length < 3) {
      el.value = o.title;
      setBad('Give the output a title.');
      return;
    }
    run(() => sendJson('PATCH', `/api/outputs/${enc(o.id)}`, { field: 'Title', value: v }), 'Output renamed', undefined, () => {
      el.value = o.title;
    });
  };
  return (
    <section className="ed-sec">
      <h3>
        Outputs{' '}
        <Link to="/outputs" search={{ node: n.id }} className="small">open in Outputs ↗</Link>
      </h3>
      {list.length === 0 && <p className="muted small">None yet.</p>}
      {list.map((o) => (
        <div key={o.id} className={`ed-out ${o.done ? 'done' : ''}`}>
          <span className="o-diamond" aria-hidden>{o.done ? '◆' : '◇'}</span>{' '}
          {canWrite ? (
            <input
              key={`${o.id}:${o.title}`}
              className="ed-out-t"
              defaultValue={o.title}
              maxLength={300}
              title="Rename this output"
              aria-label={`Title of output ${o.title}`}
              onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
              onBlur={(e) => rename(o, e.target)}
            />
          ) : (
            o.title
          )}
          <span className="muted small"> · {o.kind} · {o.stage}</span>
        </div>
      ))}
      {bad && <p className="error small">{bad}</p>}
      {b.outputsAccess === 'write' && (
        <form
          className="ed-add"
          onSubmit={(e) => {
            e.preventDefault();
            if (title.trim().length < 3) return;
            // Only what was sent: a next title typed while this one was saving stays.
            run(() => sendJson('POST', '/api/outputs', { nodeId: n.id, title: title.trim(), kind }), 'Output added', () => setTitle((t) => (t === title ? '' : t)));
          }}
        >
          <input placeholder="+ output title…" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} />
          <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Kind">
            {OUTPUT_KINDS.map((k) => <option key={k}>{k}</option>)}
          </select>
          <button type="submit" className="btn small" disabled={busy || title.trim().length < 3}>Add</button>
        </form>
      )}
    </section>
  );
}

/** What became of a new action: "Added — sent to Todoist, emailed to Ana and posted in the thread." */
export function addedNote(a: ActionItem): string {
  const went = [a.todoist && 'sent to Todoist', a.mailed && `emailed to ${a.lead}`].filter(Boolean);
  return went.length ? `Added — ${went.join(', ')} and posted in the thread.` : 'Added and posted in the thread.';
}

/**
 * Next actions: list, tick done, move the deadline, change the text (✎), add — for anyone who may comment.
 * A grant gets no add form: it lists the actions linked to it (from a meeting agenda, or kept from before
 * it became a grant), to tick or re-date.
 */
export function ActionsSection({ b, n }: { b: GanttBundle; n: GanttNode }) {
  const grant = isGrant(n);
  const q = useQuery({ queryKey: ['actions', n.id], queryFn: () => getJson<ActionItem[]>(`/api/nodes/${enc(n.id)}/actions`) });
  const [say, setSay] = useState<Note | null>(null);
  // As the popover: the chart follows at once (withAction). Busy until this list is read again.
  const { save, busy } = useSave(n.id, setSay, true);
  const { leads, text, setText, lead, setLead, due, setDue, add } = useAddAction(b, n, setSay, save);
  const can = b.canComment;
  const today = hubDate();

  const call = async (f: () => Promise<ActionItem>, ok: string) => {
    if (await save(f, withAction)) setSay({ bad: false, text: ok });
  };
  // An open action whose text is being changed (✎): its text becomes a field until Enter, a click away or Escape.
  const [editing, setEditing] = useState('');
  const endEdit = (a: ActionItem, el: HTMLInputElement, keep: boolean) => {
    setEditing('');
    const v = el.value.replace(/\s+/g, ' ').trim();
    if (!keep || v === a.text) return;
    if (v.length < 3) return setSay({ bad: true, text: 'Describe the action first.' });
    void call(() => sendJson<ActionItem>('PATCH', `/api/actions/${enc(a.id)}`, { text: v }), 'Text changed.');
  };
  const list = q.data ?? [];
  const open = list.filter((a) => a.status !== 'done').length;

  return (
    <section className="ed-sec">
      <h3>Next actions</h3>
      {q.isPending && <p className="muted small">Loading…</p>}
      {q.isError && <p className="error small">{q.error.message}</p>}
      {q.data && open === 0 && <p className="muted small">{grant ? 'No open action.' : 'No open action — this task has no next step.'}</p>}
      {list.map((a) => {
        const done = a.status === 'done';
        const late = !done && a.due && a.due < today;
        // A sheet copy: Done and the deadline belong to the agenda sheet (the API refuses them).
        const edit = can && !a.sheet;
        return (
          <div key={a.id} className={`ed-act ${done ? 'done' : ''}`}>
            <label title={a.sheet ? sheetNote(a) : undefined}>
              <input
                type="checkbox"
                checked={done}
                disabled={!edit || busy}
                onChange={() => void call(() => sendJson<ActionItem>('PATCH', `/api/actions/${enc(a.id)}`, { done: !done }), done ? 'Reopened.' : 'Marked done.')}
              />
              <span>
                {editing === a.id ? (
                  <input
                    className="ed-act-text"
                    autoFocus
                    defaultValue={a.text}
                    maxLength={500}
                    aria-label="Next action"
                    // Every end goes through the blur; Escape marks it dropped first.
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') e.currentTarget.dataset.drop = '1';
                      if (e.key === 'Enter' || e.key === 'Escape') e.currentTarget.blur();
                    }}
                    onBlur={(e) => endEdit(a, e.currentTarget, !e.currentTarget.dataset.drop)}
                  />
                ) : (
                  a.text
                )}
                <span className="muted small">
                  {' '}
                  {a.lead}
                  {a.due && <> · <span className={late ? 'error' : ''}>{late ? 'overdue' : 'deadline'} {fmtDay(a.due)}</span></>}
                  {a.todoist && ' · in Todoist'}
                  {a.sheet && ` · ${a.sheet} sheet`}
                </span>
              </span>
            </label>
            {!done && edit && editing !== a.id && (
              <button type="button" className="link small ed-act-edit" disabled={busy} title="Change the text" aria-label={`Change the text of “${a.text}”`} onClick={() => setEditing(a.id)}>
                ✎
              </button>
            )}
            {!done && edit && (
              <input
                key={`${a.id}:${a.due}`}
                type="date"
                defaultValue={a.due}
                title="Change the deadline"
                aria-label="Deadline"
                disabled={busy}
                onBlur={(e) => {
                  const v = dueOnBlur(e.target, a.due, setSay);
                  if (v !== null) void call(() => sendJson<ActionItem>('PATCH', `/api/actions/${enc(a.id)}`, { due: v }), v ? 'Deadline changed.' : 'Deadline removed.');
                }}
              />
            )}
          </div>
        );
      })}
      {can && !grant && (
        <form
          className="ed-act-add"
          onSubmit={async (e) => {
            e.preventDefault();
            const a = await add();
            if (a) setSay({ bad: false, text: addedNote(a) });
          }}
        >
          <input placeholder="+ next action…" maxLength={500} value={text} onChange={(e) => setText(e.target.value)} />
          <div className="ed-add">
            <select value={lead} onChange={(e) => setLead(e.target.value)} aria-label="Lead">
              {leads.map((p) => <option key={p}>{p}</option>)}
            </select>
            <input type="date" value={due} onChange={(e) => setDue(e.target.value)} aria-label="Deadline" />
            <button type="submit" className="btn small" disabled={busy}>Add</button>
          </div>
        </form>
      )}
      {say && <p className={`small ${say.bad ? 'error' : 'ok'}`}>{say.text}</p>}
    </section>
  );
}

function TeamSection({ b, n, ed, run, busy }: { b: GanttBundle; n: GanttNode; ed: boolean; run: Run; busy: boolean }) {
  const [person, setPerson] = useState('');
  const [role, setRole] = useState('contributes');
  const team = b.assignments
    .filter((a) => a.nodeId === n.id)
    .sort((x, y) => byRole(x, y) || x.person.localeCompare(y.person));
  if (!ed) {
    const groups = peopleByRole(b, n.id);
    return (
      <section className="ed-sec">
        <h3>People &amp; roles</h3>
        {groups.length === 0 && <p className="muted small">Nobody assigned.</p>}
        {groups.map((g) => (
          <p key={g.role} className="roles">
            <span className="role">{g.role}</span> {g.people.join(', ')}
          </p>
        ))}
      </section>
    );
  }
  return (
    <section className="ed-sec">
      <h3>People &amp; roles</h3>
      {team.length === 0 && <p className="muted small">Nobody assigned yet.</p>}
      {team.map((a) => (
        <div key={a.id} className="ed-mbr">
          <span className="ed-mbr-name">{a.person}</span>
          <select value={a.role} disabled={busy} aria-label={`Role of ${a.person}`} onChange={(e) => run(() => sendJson('PATCH', `/api/assignments/${enc(a.id)}`, { field: 'Role', value: e.target.value }), 'Role updated')}>
            {ROLE_OPTIONS.map((r) => <option key={r}>{r}</option>)}
          </select>
          <input
            key={`${a.id}:${a.hours}`}
            className="ed-hrs"
            type="number"
            min={0}
            step={0.5}
            defaultValue={a.hours ?? ''}
            title="Hours per week"
            aria-label={`Hours per week of ${a.person}`}
            onBlur={(e) => {
              const v = changedValue(e.target, String(a.hours ?? ''));
              if (v !== null) run(() => sendJson('PATCH', `/api/assignments/${enc(a.id)}`, { field: 'HrsWkCommitted', value: v }), 'Hours updated');
            }}
          />
          <button type="button" className="ed-x" disabled={busy} aria-label={`Remove ${a.person}`} onClick={() => window.confirm(`Remove ${a.person} from “${n.label}”?`) && run(() => sendJson('DELETE', `/api/assignments/${enc(a.id)}`), `${a.person} removed`)}>×</button>
        </div>
      ))}
      <div className="ed-add">
        <select value={person} onChange={(e) => setPerson(e.target.value)} aria-label="Person to add">
          <option value="">+ person…</option>
          {b.people.map((p) => p.name).sort().map((p) => <option key={p}>{p}</option>)}
        </select>
        <select value={role} onChange={(e) => setRole(e.target.value)} aria-label="Role">
          {ROLE_OPTIONS.map((r) => <option key={r}>{r}</option>)}
        </select>
        <button
          type="button"
          className="btn small"
          disabled={!person || busy}
          // Only what was sent: a next person picked while this one was saving stays.
          onClick={() => run(() => sendJson('POST', '/api/assignments', { nodeId: n.id, personName: person, role }), `${person} added`, () => setPerson((p) => (p === person ? '' : p)))}
        >
          Add
        </button>
      </div>
    </section>
  );
}

/** Convert to grant and Delete, each needing a second click. */
function Footer({ b, n, run, busy, onGone }: { b: GanttBundle; n: GanttNode; run: Run; busy: boolean; onGone: () => void }) {
  const [armed, setArmed] = useState<'' | 'convert' | 'delete'>('');
  const grant = isGrant(n);
  const arm = (k: 'convert' | 'delete') => {
    setArmed(k);
    setTimeout(() => setArmed((a) => (a === k ? '' : a)), 4000);
  };
  if (grant && !b.canGrant) return null;
  return (
    <div className="ed-foot">
      {!grant && b.canGrant && (
        <button
          type="button"
          className={`btn small ${armed === 'convert' ? 'armed' : 'ghost'}`}
          disabled={busy}
          onClick={() => (armed === 'convert' ? run(() => sendJson('POST', `/api/nodes/${enc(n.id)}/convert-to-grant`), `${n.label} is now a grant`) : arm('convert'))}
        >
          {armed === 'convert' ? 'Confirm — leaves the task list' : 'Convert to grant'}
        </button>
      )}
      <button
        type="button"
        className={`btn small danger ${armed === 'delete' ? 'armed' : ''}`}
        disabled={busy}
        onClick={() => (armed === 'delete' ? run(() => sendJson('DELETE', `/api/nodes/${enc(n.id)}`), `${n.label} deleted`, onGone) : arm('delete'))}
      >
        {armed === 'delete' ? 'Click again to confirm delete' : grant ? 'Delete grant' : 'Delete task'}
      </button>
    </div>
  );
}
